// Components/Tabs/AdminToolsTab.jsx

import { ChartIcon, SearchIcon, ClipboardIcon, ListChecksIcon, UsersIcon, WarningIcon, TerminalIcon } from '../AdminIcon';
import React from "react";
import PropTypes from "prop-types";
import Slider from "../Slider/Slider";
import { SliderBranch } from "../SliderBranch/SliderBranch.jsx";
import AdminUserSearchTab from "./AdminUserSearchTab.jsx";
import AuditLogTab from "./AuditLogTab.jsx";
import ActiveSessionsTab from "./ActiveSessionsTab.jsx";
import LabSwipeLogTab from "./LabSwipeLogTab.jsx";
import LabStatisticsTab from "./LabStatisticsTab.jsx";
import FailedSwipesTab from "./FailedSwipesTab.jsx";
import DeveloperToolsTab from "./DeveloperToolsTab.jsx";

/**
 * AdminToolsTab Component
 * 
 * This component serves as the main navigation hub for administrative tools.
 * Only accessible to super admins. Provides access to user management,
 * audit logs, and active session management.
 * 
 * @param {Object} props - Component properties.
 * @param {Object} props.user - The current user object containing user details.
 * @param {string} props.labId - The unique identifier of the lab being managed.
 * @param {Function} props.pushTab - Function to push a new tab onto the stack.
 * @param {Function} props.revert - Function to revert the tab stack by a specified number of steps.
 */
function AdminToolsTab({ user, labId, pushTab, revert }) {
  const entries = [
    {
      icon: SearchIcon,
      text: "Find a user",
      description: "Search for a user to view their profile, tool access, badge connection, and check-in history.",
      onClick: () => pushTab(
        <AdminUserSearchTab adminRoute="search"
          key={`adminUserSearch-${labId}`}
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
        />
      ),
      disabled: false
    },
    {
      icon: ClipboardIcon,
      text: "Audit log",
      description: "Review recorded admin actions, including who performed them and when.",
      onClick: () => pushTab(
        <AuditLogTab adminRoute="audit"
          key={`auditLog-${labId}`}
          user={user}
          labId={labId}
          revert={revert}
        />
      ),
      disabled: false
    },
    {
      icon: UsersIcon,
      text: "Active sessions",
      description: "See who is currently checked in, view their selected tools, and end sessions when needed.",
      onClick: () => pushTab(
        <ActiveSessionsTab adminRoute="sessions"
          key={`activeSessions-${labId}`}
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
        />
      ),
      disabled: false
    },
    {
      icon: ListChecksIcon,
      text: "Check in log",
      description: "Review past check-ins, filter by date, inspect tool use, and export the results to CSV.",
      onClick: () => pushTab(
        <LabSwipeLogTab adminRoute="log"
          key={`labSwipeLog-${labId}`}
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
        />
      ),
      disabled: false
    },
    {
      icon: ChartIcon,
      text: "Space statistics",
      description: "Explore visits, tool usage, and daily activity for a selected date range.",
      onClick: () => pushTab(
        <LabStatisticsTab adminRoute="statistics"
          key={`labStatistics-${labId}`}
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
        />
      ),
      disabled: false
    },
    {
      icon: WarningIcon,
      text: "Failed check-ins",
      description: "Review unsuccessful check-in attempts and the reasons they failed.",
      onClick: () => pushTab(
        <FailedSwipesTab adminRoute="failed"
          key={`failedSwipes-${labId}`}
          user={user}
          labId={labId}
          pushTab={pushTab}
          revert={revert}
        />
      ),
      disabled: false
    },
    {
      // Developer tools (DBG-F1): a nested hub for operational-diagnostics
      // tools (currently just Email Debugging). Super-admin only, matching
      // the backend's _require_super_admin() gate on /admin/debug/*.
      icon: TerminalIcon,
      text: "Developer tools",
      description: "Open operational diagnostics, including email delivery checks. Available to super admins.",
      onClick: () => pushTab(
        <DeveloperToolsTab adminRoute="developerTools"
          key={`developerTools-${labId}`}
          user={user}
          pushTab={pushTab}
          revert={revert}
        />
      ),
      disabled: user.isSuperAdmin ? false : true
    }
  ];

  return (
    <Slider
      name="Admin tools"
      navColor="rgb(220, 53, 69)"
      slide={true}
      index={0}
      onClick={() => revert(1)}
    >
      <SliderBranch entries={entries} />
    </Slider>
  );
}

AdminToolsTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
};

export default AdminToolsTab;
